import { fetchWhatsNew, markWhatsNewSeen } from '@api/whatsNew';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  ADMIN_JOBS_EVENT,
  OPEN_WHATS_NEW_EVENT,
  PIP_CREDITS_EVENT,
  TOUR_SELECTION_EVENT,
  hasIncompleteOnboarding,
  isAutoOpenPath,
  isSomethingElseFirst,
  isWhatsNewShortcut,
  openWhatsNew,
  publishWhatsNewCount
} from './events';
import TourSpotlight from './TourSpotlight';
import WhatsNewDialog from './WhatsNewDialog';

export const FAQ_PATH = '/tipdirect/learn';

const CONSOLE_HELP = [
  "What's new:",
  '  tripadmit.whatsNew()               everything released, newest first',
  "  tripadmit.whatsNew('2025-01-01')   as a user last here on that date would see it",
  '  or press ? anywhere outside a text field'
].join('\n');

// The console command, for demos. It only opens the dialog; the cut-off it is
// given is never saved, so it cannot change what a real user sees next time.
const installConsoleCommand = () => {
  const whatsNew = since => {
    openWhatsNew(since ? { mode: 'new', since: String(since) } : { mode: 'all' });
    return 'Opening What\'s new';
  };
  whatsNew.help = () => console.info(CONSOLE_HELP);
  window.tripadmit = { ...(window.tripadmit || {}), whatsNew };
  return () => {
    if (window.tripadmit && window.tripadmit.whatsNew === whatsNew) delete window.tripadmit.whatsNew;
  };
};

/**
 * Mounted once in containers/App, outside the route switch, so a tour can move
 * between pages without the dialog state being torn down by a remount.
 *
 * - On the first dashboard page after sign-in it asks the API what is new for
 *   this user since they last closed the dialog, and opens itself if anything
 *   is. Users away for a long time get everything since; frequent users get
 *   only what is new.
 * - The ? menu, the ? key and `tripadmit.whatsNew()` open it on demand.
 * - Closing it, however it is closed, marks the web surface as seen.
 */
export function WhatsNewHost({ user }) {
  const history = useHistory();
  const location = useLocation();
  const userId = user?.userId;
  const onboarding = hasIncompleteOnboarding(user);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('new');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [tour, setTour] = useState(null); // { steps, index }
  const lastRequest = useRef({});
  const autoChecked = useRef(null);
  // An automatic popup that is waiting for the guide's tour picker to close.
  const deferred = useRef(false);
  const openRef = useRef(false);
  openRef.current = open;

  const load = useCallback(async request => {
    lastRequest.current = request;
    setMode(request.mode || 'new');
    setLoading(true);
    setError(false);
    try {
      const result = await fetchWhatsNew(request);
      setData(result);
      return result;
    } catch (err) {
      setError(true);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Whether an automatic popup may show right now. Checked when the answer
  // arrives as well as when the request goes out: a guide with onboarding left
  // lands on /tipdirect and is redirected to /onboarding while it is in flight.
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const canAutoOpen = () => isAutoOpenPath(pathRef.current) && !isSomethingElseFirst();

  // Automatic popup: once per signed-in user per page load, on the first
  // dashboard page they reach, and never before onboarding is finished.
  useEffect(() => {
    if (!userId || onboarding || autoChecked.current === userId || !isAutoOpenPath(location.pathname)) return;
    autoChecked.current = userId;
    fetchWhatsNew({ mode: 'new' })
      .then(result => {
        publishWhatsNewCount(result.newCount);
        if (result.newCount > 0) {
          lastRequest.current = { mode: 'new' };
          setMode('new');
          setData(result);
          setError(false);
          if (canAutoOpen()) setOpen(true);
          else deferred.current = true;
        }
      })
      .catch(() => {
        // Not worth interrupting anyone over; the ? menu still works.
      });
  }, [userId, onboarding, location.pathname]);

  // A popup held back by the page it arrived on shows on the next dashboard
  // page instead.
  useEffect(() => {
    if (deferred.current && canAutoOpen()) {
      deferred.current = false;
      setOpen(true);
    }
  }, [location.pathname]);

  // A new guide's first-run tour picker wins, and so do a company admin's
  // "Jobs to do" and Product Improvement credits notice. Each announces itself on its own request, so it can arrive
  // after this popup has already opened: step aside without marking anything
  // seen, and come back when it closes.
  useEffect(() => {
    const onOtherScreen = () => {
      if (isSomethingElseFirst()) {
        // Only the automatic popup steps aside; one the user asked for stays.
        if (openRef.current && lastRequest.current.mode === 'new') {
          deferred.current = true;
          setOpen(false);
        }
        setTour(null);
      } else if (deferred.current && canAutoOpen()) {
        deferred.current = false;
        setOpen(true);
      }
    };
    window.addEventListener(TOUR_SELECTION_EVENT, onOtherScreen);
    window.addEventListener(ADMIN_JOBS_EVENT, onOtherScreen);
    window.addEventListener(PIP_CREDITS_EVENT, onOtherScreen);
    return () => {
      window.removeEventListener(TOUR_SELECTION_EVENT, onOtherScreen);
      window.removeEventListener(ADMIN_JOBS_EVENT, onOtherScreen);
      window.removeEventListener(PIP_CREDITS_EVENT, onOtherScreen);
    };
  }, []);

  // On-demand opening: the ? menu, the keyboard shortcut, the console.
  useEffect(() => {
    if (!userId) return undefined;
    const onOpen = event => {
      const request = { mode: 'all', ...(event.detail || {}) };
      setTour(null);
      setOpen(true);
      load(request);
    };
    const onKey = event => {
      if (!isWhatsNewShortcut(event) || isSomethingElseFirst()) return;
      event.preventDefault();
      openWhatsNew({ mode: 'all' });
    };
    window.addEventListener(OPEN_WHATS_NEW_EVENT, onOpen);
    window.addEventListener('keydown', onKey);
    const uninstall = installConsoleCommand();
    return () => {
      window.removeEventListener(OPEN_WHATS_NEW_EVENT, onOpen);
      window.removeEventListener('keydown', onKey);
      uninstall();
    };
  }, [userId, load]);

  const close = useCallback(() => {
    setOpen(false);
    // Only a dialog that actually showed the user something counts as seen.
    if (!error && data) {
      publishWhatsNewCount(0);
      markWhatsNewSeen().catch(() => {});
    }
  }, [error, data]);

  const goTo = route => {
    if (route && history.location.pathname !== route) history.push(route);
  };

  const showMe = item => {
    close();
    const steps = item.tour || [];
    if (!steps.length) return;
    goTo(steps[0].route);
    setTour({ steps, index: 0 });
  };

  const nextStep = () => {
    if (!tour) return;
    const index = tour.index + 1;
    if (index >= tour.steps.length) {
      setTour(null);
      return;
    }
    goTo(tour.steps[index].route);
    setTour({ ...tour, index });
  };

  const openFaq = () => {
    close();
    goTo(FAQ_PATH);
  };

  if (!userId) return null;

  return (
    <>
      <WhatsNewDialog
        open={open}
        loading={loading}
        error={error}
        data={data}
        mode={mode}
        onClose={close}
        onRetry={() => load(lastRequest.current)}
        onShowMe={showMe}
        onOpenFaq={openFaq}
      />
      {tour && (
        <TourSpotlight
          step={tour.steps[tour.index]}
          stepIndex={tour.index}
          stepCount={tour.steps.length}
          onNext={nextStep}
          onClose={() => setTour(null)}
        />
      )}
    </>
  );
}

WhatsNewHost.propTypes = {
  user: PropTypes.object
};

export default WhatsNewHost;
